import React, { useRef, useEffect, useState } from 'react';
import MapFrame from '../../components/MapFrame';
import RSVPForm         from '../../components/RSVPForm';
import Wishlist         from '../../components/Wishlist';
import Countdown        from '../../components/Countdown';
import CalendarButton   from '../../components/CalendarButton';
import FixedCTA         from '../../components/FixedCTA';
import YandexMap        from '../../components/YandexMap';
import useReveal        from '../../components/useReveal';
import BlockSubtitle    from '../../components/BlockSubtitle';
import PaidWatermark from '../../components/PaidWatermark';
import MusicPlayer from '../../components/MusicPlayer';
import './Lemon.css';

const FILTER_CSS = {
  warm:     'sepia(0.3) saturate(1.3) brightness(1.05)',
  cool:     'hue-rotate(15deg) saturate(0.9) brightness(1.05)',
  vintage:  'sepia(0.5) contrast(0.9) brightness(1.1) saturate(0.8)',
  bw:       'grayscale(1)',
  dramatic: 'contrast(1.3) brightness(0.9) saturate(1.2)',
  soft:     'brightness(1.1) saturate(0.8) contrast(0.9)',
  golden:   'sepia(0.4) brightness(1.1) saturate(1.4) hue-rotate(-10deg)',
};
const flt = p => (p?.filter && p.filter !== 'none' ? FILTER_CSS[p.filter] : '') || undefined;
const objPos = p => (p && Number.isFinite(p.focalX) && Number.isFinite(p.focalY))
  ? `${(p.focalX * 100).toFixed(1)}% ${(p.focalY * 100).toFixed(1)}%`
  : undefined;
const src = u => !u ? null : u;

const MONTH_GEN = ['Января','Февраля','Марта','Апреля','Мая','Июня','Июля','Августа','Сентября','Октября','Ноября','Декабря'];
const WEEKDAY = ['Воскресенье','Понедельник','Вторник','Среда','Четверг','Пятница','Суббота'];

function useLemonFonts() {
  useEffect(() => {
    const id = 'lemon-fonts';
    if (document.getElementById(id)) return;
    const link = document.createElement('link');
    link.id = id;
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Great+Vibes&family=Open+Sans+Condensed:wght@300;700&display=swap';
    document.head.appendChild(link);
  }, []);
}

/* Inline dresscode heart — color comes from project.dresscode_palette so
   the host couple can pick their own palette via the constructor. The
   white highlight strokes are baked in to match the Figma original. */
const HEART_PATHS = {
  main: 'M27.1852 63.8749C26.9255 64.075 26.8589 64.0423 26.7507 63.7188C26.7524 63.4438 26.6486 63.3389 26.4634 63.1696C26.0021 62.7491 25.3408 62.3551 24.8657 61.924C19.7568 57.2861 14.2698 50.9936 10.1579 45.438C3.91446 37.0033 -1.10439 25.492 0.211122 14.5642C0.68713 10.6095 2.61366 5.72221 6.31527 4.19271C6.3672 3.68846 7.25603 3.44162 7.47499 3.87976C7.83763 3.82157 8.99562 3.677 9.29248 3.71755C10.1882 3.84185 11.1489 4.02874 12.0204 4.37078C17.916 6.68222 24.8735 14.9468 28.9966 19.9734C29.3869 20.4495 29.8179 20.8594 30.1572 21.3777C31.13 19.9091 31.9556 18.3276 32.9102 16.8465C37.672 9.46 46.4245 -1.21034 55.6824 0.111989C57.9032 0.429349 59.6852 1.24479 61.0293 3.25385C65.8041 10.3944 60.8449 21.8194 57.3147 28.6232C54.3029 34.4283 50.7588 39.7352 46.679 44.6552C42.3525 49.8722 37.7041 55.0858 32.8375 59.7324C31.2797 61.2196 29.8049 62.7491 27.9727 63.8625C27.7261 63.8572 27.4171 63.9172 27.186 63.874L27.1852 63.8749Z',
  hl1:  'M53.9299 7.3222C54.5141 7.14941 54.8932 7.47911 55.2307 7.94193C55.9932 8.98657 56.1585 10.36 56.3221 11.6083C56.38 12.0526 56.1161 13.3124 56.0451 13.8201C55.66 16.5521 54.3211 19.7742 52.7676 22.0054C52.2206 22.7917 51.4486 22.0547 51.6901 21.1564C51.7204 21.0454 52.0925 20.6116 52.1877 20.4433C53.7845 17.6011 55.5492 13.5513 54.8525 10.2252C54.804 9.99507 54.6231 9.56663 54.508 9.34624C54.2172 8.7891 53.6149 8.43295 53.7066 7.70655C53.7265 7.55052 53.7966 7.36275 53.9299 7.32308V7.3222Z',
  hl2:  'M7.90864 19.8174C8.21069 20.7897 7.97529 21.8555 8.04972 22.8684C8.07655 23.2325 8.26176 23.9016 8.32667 24.2877C8.89528 27.6694 10.0039 30.9311 11.522 33.9663C11.5817 34.11 11.4346 34.7553 11.3835 34.81C11.3662 34.8285 11.0892 34.8082 11.0148 34.9607C10.9032 34.9942 10.4003 34.8346 10.3778 34.8091C10.3596 34.7879 10.3865 34.683 10.3545 34.6054C10.0619 33.8993 9.6742 33.232 9.37907 32.5267C8.23233 29.786 6.93932 25.6339 6.74459 22.7115C6.67189 21.6254 6.47197 19.5247 7.90778 19.8165L7.90864 19.8174Z',
  hl3:  'M14.5113 40.2915C14.6255 40.2571 14.9648 40.2553 15.0782 40.2888C15.2158 40.3294 16.321 42.3243 16.1643 42.6981C15.9203 43.0957 15.5784 43.227 15.1526 43.0216C14.8315 42.3825 13.9522 41.4331 14.1495 40.682C14.166 40.6194 14.4533 40.3091 14.5122 40.2915H14.5113Z',
};

function LemonHeart({ color }) {
  return (
    <svg className="lemon-heart" viewBox="-1 -1 65 66" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d={HEART_PATHS.main} fill={color} stroke="#9CA3AF" strokeWidth="0.8" strokeLinejoin="round" />
      <path d={HEART_PATHS.hl1}  fill="#FFFDF4" />
      <path d={HEART_PATHS.hl2}  fill="#FFFDF4" />
      <path d={HEART_PATHS.hl3}  fill="#FFFDF4" />
    </svg>
  );
}

function LemonNav({ blocks, menu }) {
  const links = [
    blocks.includes('schedule') && { href: '#lemon-schedule',  label: menu?.schedule || 'Программа' },
    blocks.includes('map')      && { href: '#lemon-location',  label: menu?.location || 'Локация'   },
    blocks.includes('palette')  && { href: '#lemon-dresscode', label: menu?.dresscode || 'Дресс-код' },
    blocks.includes('rsvp')     && { href: '#lemon-rsvp',      label: menu?.rsvp || 'Анкета'    },
  ].filter(Boolean);
  if (!links.length) return null;
  return (
    <nav className="lemon-nav">
      {links.map(l => <a key={l.href} href={l.href}>{l.label}</a>)}
    </nav>
  );
}

const LM_RSVP_CLASSES = {
  form:    'lemon-form',
  tabs:    'lemon-rsvp-tabs',
  tab:     'lemon-rsvp-tab',
  tabSel:  'lemon-rsvp-tab--sel',
  field:   'lemon-form-field',
  label:   'lemon-form-lbl',
  input:   'lemon-form-input',
  submit:  'lemon-btn lemon-btn--dark lemon-btn--full',
  success: 'lemon-form-ok',
  error:   'lemon-form-err',
  chip:    'lemon-chip',
  chipSel: 'lemon-chip--sel',
  chips:   'lemon-form-chips',
};
const LM_WL_CLASSES = {
  list:        'lemon-wish',
  item:        'lemon-wish-item',
  itemBorder:  '',
  row:         'lemon-wish-row',
  name:        'lemon-wish-name',
  price:       'lemon-wish-price',
  btn:         'lemon-btn lemon-btn--dark lemon-btn--full',
  btnReserved: 'lemon-btn--res',
};
const LM_WL_MODAL_CLASSES = {
  bg:         'lemon-wish-modal-bg',
  modal:      'lemon-wish-modal',
  title:      'lemon-wish-modal-title',
  subtitle:   'lemon-wish-modal-sub',
  input:      'lemon-form-input',
  error:      'lemon-form-err',
  btns:       'lemon-wish-modal-btns',
  btnCancel:  'lemon-btn lemon-btn--outline',
  btnConfirm: 'lemon-btn lemon-btn--dark',
};
const LM_CD_CLASSES = { root: 'lemon-cd', cell: 'lemon-cd-cell', num: 'lemon-cd-num', label: 'lemon-cd-lbl' };

export default function Lemon({ project, design, isPreview }) {
  const pageRef = useRef(null);
  const blockDep = project?.blocks?.length;
  useReveal(pageRef, blockDep, { revealSelector: '.lemon-reveal', inClass: 'lemon-in' });
  useLemonFonts();

  const [inPreview, setInPreview] = useState(!!isPreview);
  useEffect(() => {
    const root = pageRef.current;
    if (!root) return;
    setInPreview(!!isPreview || !!root.closest('.lp-phone-screen,.lp-template-inner,[class*="lp-"]'));
  }, [isPreview]);

  const p = project || {};
  const d = design  || {};
  const bc = p.block_content || {};

  const accent  = '#FFDE78';
  const fg      = d.palette?.primary || p.palette?.primary || '#3E588C';
  const bg      = '#FFFDF4';

  const blocks = p.blocks || [];
  const HAS_RSVP = blocks.includes('rsvp');

  const bride    = p.bride  || 'Невеста';
  const groom    = p.groom  || 'Жених';
  const tagline  = bc.header?.subtitle || p.wedding_slogan || 'Не прошло и 5-ти лет';
  const invTitle = bc.message?.title  || 'Дорогие гости';
  const invText  = p.invitation_text || bc.message?.text || '';
  const footerQuote = bc.footer?.quote || p.wedding_slogan || 'Одна любовь, другой такой и не было';

  const photos        = p.cover_photos || (p.cover_photo ? [{ url: p.cover_photo }] : []);
  const heroPhoto     = src(photos[0]?.url) || null;
  const heroPhotoMeta = photos[0] || null;

  const dcPhotos = p.dresscode_photos || [];
  /* Футер-фото независимо от главного: если не задано — футер без фото. */
  const footerPhotoObj = p.footer_photo?.url ? p.footer_photo : null;
  const footerPhoto    = src(footerPhotoObj?.url) || null;

  const date = p.wedding_date ? new Date(p.wedding_date) : null;
  const dDay   = date ? String(date.getDate()).padStart(2, '0') : '15';
  const dMonth = date ? MONTH_GEN[date.getMonth()] : 'Августа';
  const dYear  = date ? date.getFullYear() : 2026;
  const dWday  = date ? WEEKDAY[date.getDay()] : 'Суббота';
  const dTime  = p.wedding_time || '14:30';

  /* Пусто (пользователь удалил все пункты) → только заголовок и подзаголовок. */
  const schedule = p.schedule?.length ? p.schedule : [];
  const venues   = (p.venues || []).filter(v => (v.name || "").trim() || (v.address || "").trim());
  const wishlist = p.wishlist || [];

  /* Dresscode hearts — цвета из project.dresscode_palette. Если пользователь
     удалил все цвета — сердечки не показываем (без дефолтной палитры). */
  const palette = (Array.isArray(p.dresscode_palette) && p.dresscode_palette.length)
    ? p.dresscode_palette.map(it => it.color || it.hex || it).filter(Boolean)
    : [];

  const titleSchedule = bc.schedule?.title  || 'Программа';
  const titleLocation = bc.map?.title       || 'Локация';
  const titleDress    = bc.palette?.title   || 'Дресс-код';
  const titleRsvp     = bc.rsvp?.title      || 'Подтвердите присутствие';
  const titleWish     = bc.wishlist?.title  || 'Что дарить';
  const titleCD       = bc.countdown?.title || 'До нашей свадьбы осталось';
  const calBtn        = bc.calendar?.title  || 'Добавить в календарь';

  const ASSET = '/templates/lemon';

  return (
    <div className={`lemon-page${inPreview ? ' lemon-page--preview' : ''}`} ref={pageRef}
      style={{ '--lemon-accent': accent, '--lemon-fg': fg, '--lemon-bg': bg }}>
      <PaidWatermark isPaid={project?.is_paid} isPreview={inPreview} />

      {/* CTA bar at top (desktop sticky, mobile inline below hero) */}
      <a href="#lemon-rsvp" className="lemon-top-cta">
        {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
      </a>

      <LemonNav blocks={blocks} menu={bc.menu} />

      {/* HERO */}
      <section className="lemon-hero lemon-reveal" data-nophoto={heroPhoto ? undefined : '1'}>
        <div className="lemon-hero-names">
          <span className="lemon-hero-name">{bride}</span>
          <span className="lemon-hero-amp">{bc.header?.amp || '&'}</span>
          <span className="lemon-hero-name">{groom}</span>
        </div>
        {tagline && <p className="lemon-hero-tagline">{tagline}</p>}
        {heroPhoto && (
          <div className="lemon-hero-stage">
            <div className="lemon-hero-photo-oval">
              <img src={heroPhoto} alt="" style={{ filter: flt(heroPhotoMeta), objectPosition: objPos(heroPhotoMeta) }} />
            </div>
            <img className="lemon-hero-wreath" src={`${ASSET}/wreath-hero.svg`} alt="" aria-hidden="true" />
          </div>
        )}
      </section>

      {/* INVITATION LETTER */}
      <section className="lemon-sect lemon-invite lemon-reveal">
        <h2 className="lemon-h">{invTitle}</h2>
        {invText && (
          <div className="lemon-invite-body">
            {invText.split('\n').map((line, i) =>
              line.trim() ? <p key={i}>{line}</p> : <br key={i} />)}
          </div>
        )}
      </section>

      {/* DATE ROW */}
      <section className="lemon-date-sect lemon-reveal">
        <img className="lemon-branch lemon-branch--l" src={`${ASSET}/lemon-branch-left.svg`} alt="" aria-hidden="true" />
        <img className="lemon-branch lemon-branch--r" src={`${ASSET}/lemon-branch-right.svg`} alt="" aria-hidden="true" />
        <div className="lemon-date-row">
          <div className="lemon-date-side">
            <p>{dWday}</p>
            <p>{dTime}</p>
          </div>
          <span className="lemon-date-divider" />
          <p className="lemon-date-day">{dDay}</p>
          <span className="lemon-date-divider" />
          <div className="lemon-date-side">
            <p>{dMonth}</p>
            <p>{dYear}</p>
          </div>
        </div>
        <CalendarButton
          bride={bride}
          groom={groom}
          date={p.wedding_date}
          time={p.wedding_time}
          venues={p.venues}
          address={p.wedding_address}
          className="lemon-btn lemon-btn--outline">
          {calBtn}
        </CalendarButton>
      </section>

      {/* Dynamic blocks — Figma order */}
      {['map','schedule','palette','rsvp','wishlist','countdown']
        .filter(b => blocks.includes(b))
        .map(blockId => {
          switch (blockId) {
            case 'map':
              return (
                <section key="map" className="lemon-sect lemon-map-sect lemon-reveal" id="lemon-location">
                  <h2 className="lemon-h">{titleLocation}</h2>
                  <BlockSubtitle text={bc.map?.subtitle} className="lemon-p" />
                  {venues.map((v, i) => (
                    <div key={i} className="lemon-addr">
                      <img className="lemon-addr-icon" src={`${ASSET}/pin.svg`} alt="" aria-hidden="true" />
                      <span>{v.name && v.address ? `${v.name}, ${v.address}` : (v.name || v.address)}</span>
                    </div>
                  ))}
                  {!venues.length && p.wedding_address && (
                    <div className="lemon-addr">
                      <img className="lemon-addr-icon" src={`${ASSET}/pin.svg`} alt="" aria-hidden="true" />
                      <span>{p.wedding_address}</span>
                    </div>
                  )}
                  {/* Карта — только при наличии адреса (имя без адреса → без карты). */}
                  {(p.map_url || (p.wedding_address || '').trim() || venues.some(v => (v.address || '').trim())) && (
                  <div className="lemon-map-wrap">
                    {(() => {
                      if (p.map_url) {
                        return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={p.map_url} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} />;
                      }
                      const withCoords = venues.filter(v => Number.isFinite(+v.lat) && Number.isFinite(+v.lon));
                      if (withCoords.length > 0) {
                        return <YandexMap points={withCoords} singleZoom={16} region={p.region} provider={bc.map?.provider} preset="islands#yellowIcon" />;
                      }
                      const addr = venues.length > 0
                        ? venues.map(v => v.address).filter(Boolean).join(' — ')
                        : (p.wedding_address || '');
                      if (!addr) return null;
                      const s = `https://yandex.ru/map-widget/v1/?mode=search&text=${encodeURIComponent(addr)}&z=16&lang=ru_RU`;
                      return <MapFrame region={p.region} provider={bc.map?.provider} title="Карта" src={s} allowFullScreen style={{ width: '100%', height: '100%', border: 'none' }} allow="geolocation" />;
                    })()}
                  </div>
                  )}
                </section>
              );

            case 'schedule':
              return (
                <section key="schedule" className="lemon-sched-sect lemon-reveal" id="lemon-schedule">
                  <img className="lemon-champagne" src={`${ASSET}/champagne-lemons.svg`} alt="" aria-hidden="true" />
                  <div className="lemon-sched-bg-wrap">
                    <picture className="lemon-sched-bg">
                      <source media="(min-width: 900px)" srcSet={`${ASSET}/sched-bg-desktop.svg`} />
                      <img src={`${ASSET}/sched-bg-mobile.svg`} alt="" aria-hidden="true" />
                    </picture>
                    <div className="lemon-sched-card">
                      <div className="lemon-sched-inner">
                        <div className="lemon-sched-head">
                          <h2 className="lemon-h">{titleSchedule}</h2>
                          <BlockSubtitle text={bc.schedule?.subtitle || 'Чтобы вам было проще ориентироваться в течение дня, мы подготовили небольшое расписание основных событий'} className="lemon-p" />
                        </div>
                        <ul className="lemon-sched">
                          {schedule.map((it, i) => (
                            <li key={i} className="lemon-sched-row">
                              <p className="lemon-sched-time">{it.time}</p>
                              <p className="lemon-sched-desc">{it.event || it.name}</p>
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                </section>
              );

            case 'palette':
              return (
                <section key="palette" className="lemon-sect lemon-dress-sect lemon-reveal" id="lemon-dresscode">
                  <img className="lemon-butterfly" src={`${ASSET}/butterfly.svg`} alt="" aria-hidden="true" />
                  <h2 className="lemon-h">{titleDress}</h2>
                  <BlockSubtitle text={bc.palette?.subtitle || 'Вы всегда выглядите на 9 999 999 из 100, но на нашем праздновании мы бы хотели видеть вас в единой стилистике, поэтому решили ввести дресс-код. Вы же нас любите и не будете противоречить?)'} className="lemon-p" tag="div" />
                  <div className="lemon-outfits">
                    {palette.map((color, i) => (
                      <LemonHeart key={i} color={color} />
                    ))}
                  </div>
                  {dcPhotos.length > 0 && (
                    <div className="lemon-dc-photos">
                      {dcPhotos.slice(0, 6).map((ph, i) => (
                        <div key={i} className="lemon-dc-photo">
                          <img src={src(ph.url)} alt="" style={{ filter: flt(ph), objectPosition: objPos(ph) }} />
                        </div>
                      ))}
                    </div>
                  )}
                </section>
              );

            case 'rsvp':
              return (
                <section key="rsvp" className="lemon-sect lemon-rsvp-sect lemon-reveal" id="lemon-rsvp">
                  <h2 className="lemon-h">{titleRsvp}</h2>
                  <BlockSubtitle text={bc.rsvp?.subtitle || 'Будем благодарны, если вы сообщите о своём присутствии заранее — это очень важно для подготовки мероприятия'} className="lemon-p" />
                  <RSVPForm project={p} blocks={blocks} classes={LM_RSVP_CLASSES} />
                </section>
              );

            case 'wishlist':
              return (
                <section key="wishlist" className="lemon-sect lemon-wish-sect lemon-reveal">
                  <h2 className="lemon-h">{titleWish}</h2>
                  <BlockSubtitle text={bc.wishlist?.subtitle || 'Мы очень ценим вашу заботу и подготовили несколько идей, которые могут быть полезны при выборе подарка'} className="lemon-p" />
                  <Wishlist
                    items={wishlist} currency={bc.wishlist?.currency} reserveText={bc.wishlist?.reserveText}
                    slug={p.slug}
                    classes={LM_WL_CLASSES}
                    modalClasses={LM_WL_MODAL_CLASSES}
                  />
                </section>
              );

            case 'countdown':
              return (
                <section key="countdown" className="lemon-sect lemon-cd-sect lemon-reveal">
                  <img className="lemon-pair" src={`${ASSET}/lemon-pair.svg`} alt="" aria-hidden="true" />
                  <h2 className="lemon-h">{titleCD}</h2>
                  <BlockSubtitle text={bc.countdown?.subtitle || 'Совсем скоро наступит день, наполненный счастьем, красивыми моментами и самыми искренними эмоциями'} className="lemon-p" />
                  <Countdown date={p.wedding_date} time={p.wedding_time} bc={bc} classes={LM_CD_CLASSES} />
                </section>
              );

            default:
              return null;
          }
        })}

      {/* FOOTER */}
      <footer className="lemon-footer lemon-reveal">
        {footerPhoto && (
          <div className="lemon-footer-photo">
            <img src={footerPhoto} alt="" style={{ filter: flt(footerPhotoObj), objectPosition: objPos(footerPhotoObj) }} />
          </div>
        )}
        <div className="lemon-footer-quote">
          <img className="lemon-footer-wreath" src={`${ASSET}/wreath-quote.svg`} alt="" aria-hidden="true" />
          <p>{footerQuote}</p>
        </div>
      </footer>

      {HAS_RSVP && (
        <FixedCTA
          href="#lemon-rsvp"
          targetId="lemon-rsvp"
          scrollRoot={pageRef}
          className="lemon-fixed-cta">
          {bc.rsvp?.ctaText || 'Подтвердить присутствие'}
        </FixedCTA>
      )}

      {(project?.blocks || []).includes('music') && project?.music?.track?.url && (
        <MusicPlayer music={project.music} palette={project.palette} isPreview={inPreview} />
      )}
    </div>
  );
}
